import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Close, WhatsApp } from "@mui/icons-material";
import { getCommerceWhatsAppTerminalsService } from "modules/jarvis/services/whatsappService";
import {
  whatsappForm,
  whatsappFormErrors,
  optionalWhatsappTerminal,
} from "../../model/whatsappModel";
export default function WhatsappDialog({
  open,
  onClose,
  onSave,
  configuracion,
  attributes,
  availableComercios = [],
  loading = false,
  loadError,
  onRetry,
}) {
  const editing = !!configuracion;
  const [form, setForm] = useState(() => whatsappForm(configuracion));
  const [errors, setErrors] = useState({}),
    [review, setReview] = useState(false),
    [busy, setBusy] = useState(false),
    [result, setResult] = useState(null);
  const [terminals, setTerminals] = useState([]),
    [loadingTerminals, setLoadingTerminals] = useState(false),
    [terminalError, setTerminalError] = useState(null),
    [attempt, setAttempt] = useState(0);
  const locked = useRef(false),
    alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    if (open) {
      setForm(whatsappForm(configuracion));
      setErrors({});
      setReview(false);
      setBusy(false);
      setResult(null);
      locked.current = false;
    }
  }, [open, configuracion]);
  useEffect(() => {
    let active = true;
    setTerminals([]);
    setTerminalError(null);
    setLoadingTerminals(false);
    if (!open || !form.idcommerce)
      return () => {
        active = false;
      };
    setLoadingTerminals(true);
    (async () => {
      try {
        const response = await getCommerceWhatsAppTerminalsService(form.idcommerce, attempt > 0);
        if (!active) return;
        if (response.success) setTerminals(response.data);
        else setTerminalError(response.error || "No se pudieron cargar las terminales.");
      } catch (err) {
        if (active) setTerminalError(err.message);
      } finally {
        if (active) setLoadingTerminals(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, form.idcommerce, attempt]);
  const groups = Array.isArray(attributes?.group_wp_dis) ? attributes.group_wp_dis : [];
  useEffect(() => {
    if (!editing || !form.groupName || form.chatId) return;
    const matches = (Array.isArray(attributes?.group_wp_dis) ? attributes.group_wp_dis : []).filter(
      (group) => group.groupName === form.groupName
    );
    if (matches.length === 1 && matches[0].chatId)
      setForm((prev) => ({ ...prev, chatId: matches[0].chatId }));
  }, [editing, attributes, form.groupName, form.chatId]);
  const optional = optionalWhatsappTerminal(form.nameworkflows);
  const close = () => {
    if (!locked.current) onClose();
  };
  const change = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: null }));
    setResult(null);
  };
  const prepare = (event) => {
    event.preventDefault();
    if (locked.current || loading || loadingTerminals || loadError) return;
    const next = whatsappFormErrors(form);
    setErrors(next);
    if (!Object.keys(next).length) setReview(true);
  };
  const save = async () => {
    if (locked.current || result?.success || result?.uncertain) return;
    locked.current = true;
    setBusy(true);
    setResult(null);
    try {
      const response = await onSave({
        ...form,
        idcommerce: Number(form.idcommerce),
        idterminal: form.idterminal ? Number(form.idterminal) : null,
      });
      if (alive.current)
        setResult(
          response || {
            success: false,
            uncertain: true,
            error: "No se recibió confirmación del guardado.",
          }
        );
    } catch (err) {
      if (alive.current) setResult({ success: false, error: err.message });
    } finally {
      locked.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const flag = (field, label, color, bg) => (
    <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #dce5ed", bgcolor: bg }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1, color }}>{label}</Typography>
      <ToggleButtonGroup
        fullWidth
        exclusive
        value={form[field]}
        disabled={busy || loading}
        onChange={(_, value) => {
          if (value !== null) change(field, value);
        }}
        sx={{
          "& .MuiToggleButton-root": {
            minHeight: 42,
            textTransform: "none",
            color: "#506b80",
            fontSize: 13,
          },
          "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover": {
            bgcolor: form[field] ? color : "#596d80",
            color: "#fff",
          },
        }}
      >
        <ToggleButton value={true}>Activo</ToggleButton>
        <ToggleButton value={false}>Inactivo</ToggleButton>
      </ToggleButtonGroup>
      {errors[field] && (
        <Typography sx={{ fontSize: 12, mt: 1, color: "#a5394b" }}>{errors[field]}</Typography>
      )}
    </Box>
  );
  const select = (field, label, options) => (
    <TextField
      fullWidth
      select
      name={field}
      label={label}
      value={form[field]}
      disabled={busy || loading || !!loadError}
      error={!!errors[field]}
      helperText={errors[field]}
      onChange={(e) => change(field, e.target.value)}
    >
      {[...new Set([...options, ...(form[field] ? [form[field]] : [])])].map((value) => (
        <MenuItem key={value} value={value}>
          {value}
        </MenuItem>
      ))}
    </TextField>
  );
  const terminalOptions = [...terminals];
  if (
    editing &&
    form.idterminal &&
    !terminals.some((terminal) => String(terminal.id) === form.idterminal)
  )
    terminalOptions.push({
      id: form.idterminal,
      nombre: form.terminalname || `Terminal ${form.idterminal}`,
    });
  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "90dvh", bgcolor: "#f5f8fb" } }}
    >
      <DialogTitle
        sx={{
          p: 2.5,
          background: "linear-gradient(110deg, #193d50, #117565)",
          color: "#fff",
          display: "flex",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <WhatsApp />
        <Box sx={{ flex: 1 }}>
          <Typography component="h2" sx={{ fontSize: 19, color: "#fff", fontWeight: 700 }}>
            {review
              ? "Revisar configuración WhatsApp"
              : editing
              ? "Editar configuración WhatsApp"
              : "Agregar configuración WhatsApp"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#d3e9e2" }}>
            {editing
              ? `${configuracion.nombreComercio} · ID ${configuracion.idCommerce}`
              : "Comercio, terminal y destino de las alertas"}
          </Typography>
        </Box>
        <IconButton
          aria-label="Cerrar configuración"
          disabled={busy}
          onClick={close}
          sx={{ color: "#fff" }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: { xs: 2, sm: 3 },
          "& .MuiOutlinedInput-root": { minHeight: 52, fontSize: 14, bgcolor: "#fff" },
          "& .MuiSelect-icon": { display: "block", color: "#59748a" },
        }}
      >
        {loading && (
          <Box sx={{ display: "flex", gap: 1.5, my: 2 }}>
            <CircularProgress size={22} />
            <Typography sx={{ fontSize: 14 }}>Cargando opciones…</Typography>
          </Box>
        )}
        {loadError && (
          <Alert
            severity="error"
            sx={{ my: 2 }}
            action={
              <Button disabled={loading} color="inherit" onClick={onRetry}>
                Reintentar
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}
        {result && (
          <Alert
            sx={{ my: 2 }}
            severity={
              result.success
                ? result.refreshWarning
                  ? "warning"
                  : "success"
                : result.uncertain
                ? "warning"
                : "error"
            }
          >
            {result.success
              ? result.refreshWarning || "Configuración guardada correctamente."
              : result.error}
            {result.uncertain
              ? " Actualiza el listado antes de volver a intentar el guardado."
              : ""}
          </Alert>
        )}
        {review ? (
          <Box
            sx={{ mt: 2, p: 2.5, bgcolor: "#fff", border: "1px solid #dce5ed", borderRadius: 2 }}
          >
            <Typography sx={{ color: "#506b80", fontSize: 14, mb: 2 }}>
              Verifica los valores antes de confirmar.
            </Typography>
            <Grid container spacing={2.5}>
              {[
                ["Comercio", `${form.namecommerce} · ID ${form.idcommerce}`],
                ["Terminal", form.terminalname || "Sin terminal"],
                ["Grupo NOC", form.groupnoc],
                ["Workflow", form.nameworkflows],
                ["Grupo WhatsApp", form.groupName],
                ["Chat ID", form.chatId],
                ["Estado", form.status ? "Activo" : "Inactivo"],
                ["Pulso", form.pulse ? "Activo" : "Inactivo"],
              ].map(([label, value]) => (
                <Grid key={label} item xs={12} sm={6}>
                  <Typography sx={{ fontSize: 12, color: "#657d91", mb: 0.5 }}>{label}</Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "#254459",
                      fontWeight: 600,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {value || "—"}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <Box component="form" id="whatsapp-form" onSubmit={prepare} sx={{ mt: 3 }}>
            {!editing && (
              <Autocomplete
                options={availableComercios}
                disabled={loading || busy}
                value={availableComercios.find((row) => String(row.id) === form.idcommerce) || null}
                getOptionLabel={(row) => `${row.nombre} · ID ${row.id}`}
                isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                onChange={(_, row) => {
                  setForm((prev) => ({
                    ...prev,
                    idcommerce: row ? String(row.id) : "",
                    namecommerce: row?.nombre || "",
                    idterminal: "",
                    terminalname: "",
                  }));
                  setErrors((prev) => ({ ...prev, idcommerce: null }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Comercio del catálogo"
                    error={!!errors.idcommerce}
                    helperText={errors.idcommerce}
                  />
                )}
              />
            )}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="ID del comercio"
                  value={form.idcommerce}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={9}>
                <TextField
                  fullWidth
                  label="Nombre del comercio"
                  value={form.namecommerce}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {select("groupnoc", "Grupo NOC", attributes?.groupnoc || [])}
              </Grid>
              <Grid item xs={12} sm={6}>
                {select(
                  "nameworkflows",
                  "Workflow",
                  (attributes?.workflows || []).map((value) =>
                    typeof value === "string" ? value : value.nameworkflows
                  )
                )}
              </Grid>
              <Grid item xs={12}>
                {terminalError && (
                  <Alert
                    severity="error"
                    sx={{ mb: 1 }}
                    action={
                      <Button
                        disabled={loadingTerminals}
                        color="inherit"
                        onClick={() => setAttempt((value) => value + 1)}
                      >
                        Reintentar
                      </Button>
                    }
                  >
                    {terminalError}
                  </Alert>
                )}
                <TextField
                  fullWidth
                  select
                  name="idterminal"
                  label={`Terminal${optional ? " (opcional)" : ""}`}
                  value={form.idterminal}
                  disabled={loadingTerminals || busy || !form.idcommerce}
                  error={!!errors.idterminal}
                  helperText={
                    errors.idterminal ||
                    (loadingTerminals
                      ? "Cargando terminales…"
                      : optional
                      ? "Este workflow permite continuar sin terminal."
                      : "Selecciona una terminal del comercio.")
                  }
                  onChange={(e) => {
                    const terminal = terminalOptions.find(
                      (value) => String(value.id) === e.target.value
                    );
                    setForm((prev) => ({
                      ...prev,
                      idterminal: e.target.value,
                      terminalname: terminal?.nombre || "",
                    }));
                    setErrors((prev) => ({ ...prev, idterminal: null }));
                  }}
                >
                  {optional && <MenuItem value="">Sin terminal</MenuItem>}
                  {terminalOptions.map((terminal) => (
                    <MenuItem key={terminal.id} value={String(terminal.id)}>
                      {terminal.nombre} · ID {terminal.id}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                {editing ? (
                  <TextField
                    fullWidth
                    label="Grupo WhatsApp"
                    value={form.groupName}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                  />
                ) : (
                  <Autocomplete
                    options={groups}
                    value={
                      groups.find(
                        (group) =>
                          group.groupName === form.groupName && group.chatId === form.chatId
                      ) || null
                    }
                    getOptionLabel={(group) => group.groupName || ""}
                    isOptionEqualToValue={(a, b) => a.chatId === b.chatId}
                    disabled={loading || busy}
                    onChange={(_, group) =>
                      setForm((prev) => ({
                        ...prev,
                        groupName: group?.groupName || "",
                        chatId: group?.chatId || "",
                      }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Grupo WhatsApp (opcional)" />
                    )}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Chat ID"
                  value={form.chatId}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {flag("status", "Estado de la configuración", "#206346", "#edf8f3")}
              </Grid>
              <Grid item xs={12} sm={6}>
                {flag("pulse", "Pulso", "#51479c", "#f1effc")}
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, bgcolor: "#fff", borderTop: "1px solid #dce5ed", gap: 1 }}>
        <Button onClick={close} disabled={busy} sx={{ "&&": { color: "#365b76" } }}>
          {result?.success || result?.uncertain ? "Cerrar" : "Cancelar"}
        </Button>
        {review && !result?.success && !result?.uncertain && (
          <Button
            disabled={busy}
            onClick={() => {
              setReview(false);
              setResult(null);
            }}
            sx={{ "&&": { color: "#365b76" } }}
          >
            Volver a editar
          </Button>
        )}
        {!result?.success && !result?.uncertain && (
          <Button
            type={review ? "button" : "submit"}
            form={review ? undefined : "whatsapp-form"}
            onClick={review ? save : undefined}
            variant="contained"
            disabled={
              busy || loading || loadingTerminals || !!loadError || (!optional && !!terminalError)
            }
            sx={{
              "&&": { bgcolor: "#17634f", color: "#fff" },
              "&&.Mui-disabled": { bgcolor: "#e1e8ee", color: "#75889a" },
            }}
          >
            {busy ? <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} /> : null}
            {review ? "Confirmar configuración" : "Revisar configuración"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
WhatsappDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  configuracion: PropTypes.object,
  attributes: PropTypes.object,
  availableComercios: PropTypes.array,
  loading: PropTypes.bool,
  loadError: PropTypes.string,
  onRetry: PropTypes.func,
};
